/**
 * KPI tiles row (NAU-247) — four whole-nursery tiles replacing the old five.
 * Icon treatment matches the product reference: bold outline Lucide glyphs in a
 * white circle with a coloured ring halo.
 */

import { Badge } from "@package/subframe/components/Badge";
import { LinkButton } from "@package/subframe/components/LinkButton";
import { FeatherArrowRight } from "@subframe/core";
import { AlertTriangle, Check, Flag } from "lucide-react";

import type { DashboardKpis } from "@/lib/dashboard/dashboard-overview";
import {
  type DashboardPeriod,
  periodLabels,
  previousPeriodLabels,
} from "@/lib/dashboard/period";
import {
  DASHBOARD_STATUS_META,
  type DashboardStatus,
} from "@/lib/dashboard/traffic-light";

import { NaudRingBadge, toneFromStatus } from "./NaudIcon";
import { RadialProgress } from "./RadialProgress";

interface StatTileProps {
  iconNode: React.ReactNode;
  value: number;
  title: string;
  subtitle: string;
  status: DashboardStatus;
  onViewAll: () => void;
}

function StatTile({
  iconNode,
  value,
  title,
  subtitle,
  status,
  onViewAll,
}: StatTileProps) {
  const meta = DASHBOARD_STATUS_META[status];
  return (
    <div
      className={`flex grow shrink-0 basis-0 flex-col justify-between gap-4 rounded-md border border-solid ${meta.borderClass} ${meta.tintClass} p-5`}
    >
      {/* Top: icon + big number */}
      <div className="flex items-center gap-4">
        {iconNode}
        <span className="text-heading-1 font-heading-1 leading-none text-neutral-900">
          {value}
        </span>
      </div>

      {/* Middle: title + subtitle */}
      <div className="flex flex-col gap-1">
        <span className="text-body-bold font-body-bold text-neutral-900">
          {title}
        </span>
        <span className="text-caption font-caption text-subtext-color">
          {subtitle}
        </span>
      </div>

      {/* Status pill on its own line */}
      <div>
        <Badge variant={meta.badgeVariant}>{meta.label}</Badge>
      </div>

      {/* Footer: divider + View all */}
      <div className="flex justify-end border-t border-solid border-neutral-200 pt-3">
        <LinkButton
          variant="brand"
          size="small"
          iconRight={<FeatherArrowRight />}
          onClick={onViewAll}
        >
          View all
        </LinkButton>
      </div>
    </div>
  );
}

function CompletionTile({
  kpis,
  period,
}: {
  kpis: DashboardKpis;
  period: DashboardPeriod;
}) {
  const { completion } = kpis;
  const delta = completion.deltaVsLastWeek;
  // Ring coloured by status (green/red only in v1).
  const meta = DASHBOARD_STATUS_META[completion.status];

  // Delta colour: positive → green, negative → red, no change → neutral.
  const deltaColor =
    delta === null || delta === 0
      ? "text-subtext-color"
      : delta > 0
        ? "text-success-700"
        : "text-error-700";
  const deltaArrow = delta && delta > 0 ? "↑" : delta && delta < 0 ? "↓" : "";

  return (
    <div className="flex grow shrink-0 basis-0 flex-col items-center gap-3 rounded-md border border-solid border-primary-navy-100 bg-white p-5">
      <div className="flex w-full flex-col">
        <span className="text-body-bold font-body-bold text-primary-navy-800">
          Completion rate
        </span>
        <span className="text-caption font-caption text-subtext-color">
          {periodLabels[period]}
        </span>
      </div>
      <RadialProgress
        value={completion.rate}
        colorVar={meta.colorVar}
        trackVar="var(--color-neutral-100)"
      >
        <span className={`text-heading-1 font-heading-1 ${meta.textClass}`}>
          {completion.rate}%
        </span>
      </RadialProgress>
      {delta !== null && (
        <span className={`text-caption-bold font-caption-bold ${deltaColor}`}>
          {deltaArrow} {Math.abs(delta)}% vs {previousPeriodLabels[period]}
        </span>
      )}
      <span className="text-caption font-caption text-subtext-color">
        {completion.completed} of {completion.total} completed
      </span>
    </div>
  );
}

/** Four muted placeholders shown while the status query loads, so the cards
 *  don't flash a falsely-reassuring "100% / all green" before data arrives. */
function SkeletonTiles() {
  return (
    <div className="flex w-full items-stretch gap-4 mobile:grid mobile:grid-cols-2">
      {Array.from({ length: 4 }).map((_, i) => (
        <div
          key={i}
          className="flex grow shrink-0 basis-0 flex-col gap-4 rounded-md border border-solid border-neutral-100 p-5"
        >
          <div className="h-9 w-9 animate-pulse rounded-full bg-neutral-100" />
          <div className="h-6 w-1/2 animate-pulse rounded bg-neutral-100" />
          <div className="h-3 w-2/3 animate-pulse rounded bg-neutral-100" />
        </div>
      ))}
    </div>
  );
}

interface StatTilesProps {
  kpis: DashboardKpis;
  period: DashboardPeriod;
  loading?: boolean;
  onViewMissed: () => void;
  onViewIssues: () => void;
  onViewActions: () => void;
}

export function StatTiles({
  kpis,
  period,
  loading,
  onViewMissed,
  onViewIssues,
  onViewActions,
}: StatTilesProps) {
  if (loading) {
    return <SkeletonTiles />;
  }
  return (
    <div className="flex w-full items-stretch gap-4 mobile:grid mobile:grid-cols-2">
      <CompletionTile kpis={kpis} period={period} />
      <StatTile
        iconNode={
          <NaudRingBadge
            icon={AlertTriangle}
            tone={toneFromStatus(kpis.missedOverdue.status)}
          />
        }
        value={kpis.missedOverdue.value}
        title="Missed/Overdue"
        subtitle="Overdue or missed checks"
        status={kpis.missedOverdue.status}
        onViewAll={onViewMissed}
      />
      <StatTile
        iconNode={
          <NaudRingBadge
            icon={Flag}
            tone={toneFromStatus(kpis.issuesFlagged.status)}
          />
        }
        value={kpis.issuesFlagged.value}
        title="Issues flagged"
        subtitle="Items requiring attention"
        status={kpis.issuesFlagged.status}
        onViewAll={onViewIssues}
      />
      <StatTile
        iconNode={
          <NaudRingBadge
            icon={Check}
            tone={toneFromStatus(kpis.openActions.status)}
          />
        }
        value={kpis.openActions.value}
        title="Open actions"
        subtitle="Awaiting action"
        status={kpis.openActions.status}
        onViewAll={onViewActions}
      />
    </div>
  );
}
